import {
  Component,
  input,
  output,
  signal,
  computed,
  effect,
  ElementRef,
  viewChild,
  HostListener,
} from '@angular/core';
import { DropdownItem } from '../dropdown/dropdown.component';

@Component({
  selector: 'app-search-field',
  templateUrl: './search-field.component.html',
  styleUrls: ['./search-field.component.scss'],
  standalone: false,
})
export class SearchFieldComponent {
  /**
   * The label text for the field
   */
  label = input<string>('');

  /**
   * Whether the field is required
   */
  required = input<boolean>(false);

  /**
   * Whether to show the required text in the header
   * Defaults to true for backward compatibility
   */
  showRequiredText = input<boolean>(true);

  /**
   * The placeholder text for the search input
   */
  placeholder = input<string>('Type to search');

  /**
   * The placeholder text to show when the field has an error
   */
  errorPlaceholder = input<string>('');

  /**
   * The current value of the search field
   */
  value = input<string>('');

  /**
   * Text to display for the required field message
   */
  requiredText = input<string>('*This field is required.');

  /**
   * Array of dropdown items to display when searching
   */
  dropdownItems = input<DropdownItem[]>([]);

  /**
   * Maximum height for the dropdown before scrolling
   */
  dropdownMaxHeight = input<string>('300px');

  /**
   * Whether to show the search icon when the field is empty
   */
  showSearchIcon = input<boolean>(false);

  /**
   * Whether the field has a validation error
   */
  hasError = input<boolean>(false);

  /**
   * The error message to display
   */
  errorMessage = input<string>('');

  /**
   * Whether the search field is disabled
   */
  disabled = input<boolean>(false);

  /**
   * Emits when the search value changes
   */
  valueChange = output<string>();

  /**
   * Emits when the user types in the search field
   */
  search = output<string>();

  /**
   * Emits when an item is selected from the dropdown
   */
  itemSelected = output<DropdownItem>();

  /**
   * Emits when Enter key is pressed (form submission)
   */
  submitted = output<string>();

  /**
   * Emits when the input loses focus (blur event)
   */
  blurred = output<void>();

  /**
   * Reference to the input element
   */
  inputElement = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  /**
   * Reference to the search field container
   */
  searchFieldContainer = viewChild<ElementRef>('searchFieldContainer');

  /**
   * Reference to the dropdown component
   */
  dropdownComponent = viewChild<any>('dropdown');

  /**
   * Whether the dropdown is currently open
   */
  isDropdownOpen = signal<boolean>(false);

  /**
   * The current search text
   */
  searchText = signal<string>('');

  /**
   * Whether dropdown should open above the input (for positioning)
   */
  dropdownOpenAbove = signal<boolean>(false);

  /**
   * Whether the field has been touched/blurred
   */
  private isTouched = signal<boolean>(false);

  /**
   * Whether to show the error state
   * Shows error only when field has error, is touched, and dropdown is closed
   */
  showError = computed(() => {
    return this.hasError() && this.isTouched() && !this.isDropdownOpen();
  });

  /**
   * The effective placeholder to display
   * Shows errorPlaceholder when in error state, otherwise shows regular placeholder
   */
  effectivePlaceholder = computed(() => {
    return this.showError() && this.errorPlaceholder()
      ? this.errorPlaceholder()
      : this.placeholder();
  });

  /**
   * Debounce timer for search input
   */
  private debounceTimer: any = null;

  /**
   * Filtered dropdown items based on search text
   */
  filteredItems = computed(() => {
    const items = this.dropdownItems();
    const searchTerm = this.searchText().toLowerCase().trim();

    // If no search term, return all items
    if (!searchTerm) {
      return items;
    }

    // Filter items based on search term
    return items.filter(item =>
      item.label.toLowerCase().includes(searchTerm)
    );
  });

  constructor() {
    // Sync value input with searchText signal
    effect(() => {
      const val = this.value();
      this.searchText.set(val);
    });
  }

  /**
   * Handles input event with debouncing
   */
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    this.isTouched.set(false);
    this.searchText.set(value);

    // Keep dropdown open during input (filtering will happen in filteredItems computed)
    if (!this.isDropdownOpen()) {
      this.checkDropdownPosition();
      this.isDropdownOpen.set(true);
    }

    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set new timer with 300ms debounce
    this.debounceTimer = setTimeout(() => {
      this.valueChange.emit(value);
      this.search.emit(value);
    }, 300);
  }

  /**
   * Handles focus event on the input
   */
  onInputFocus(): void {
    this.isTouched.set(false);
    this.checkDropdownPosition();
    // Open dropdown immediately on focus, regardless of search text
    this.isDropdownOpen.set(true);
  }

  /**
   * Handles blur event on the input
   */
  onInputBlur(): void {
    // Restore to selected value if it exists and differs from current text
    const currentValue = this.value();
    const currentSearchText = this.searchText();

    if (currentValue && currentSearchText !== currentValue) {
      this.searchText.set(currentValue);
      const inputEl = this.inputElement();
      if (inputEl) {
        inputEl.nativeElement.value = currentValue;
      }
    }

    this.isTouched.set(true);
    this.blurred.emit();
  }

  /**
   * Handles keyboard events on the input
   */
  onInputKeyDown(event: KeyboardEvent): void {
    const dropdown = this.dropdownComponent();

    // Handle Enter key press
    if (event.key === 'Enter') {
      event.preventDefault();

      // If dropdown is open and has a focused item, let dropdown handle Enter
      if (this.isDropdownOpen() && dropdown && dropdown.focusedItem()) {
        dropdown.onKeyDown(event);
        return;
      }

      // Otherwise, close dropdown if open and emit submitted event
      if (this.isDropdownOpen()) {
        this.closeDropdown();
      }

      // Emit submitted event for search submission
      this.submitted.emit(this.searchText());
      return;
    }

    // For other keys, only handle if dropdown is open
    if (!dropdown || !this.isDropdownOpen()) {
      return;
    }

    // Handle Escape key - restore to selected value before closing
    if (event.key === 'Escape') {
      event.preventDefault();

      // Restore to selected value if it exists
      const currentValue = this.value();
      if (currentValue) {
        this.searchText.set(currentValue);
        const inputEl = this.inputElement();
        if (inputEl) {
          inputEl.nativeElement.value = currentValue;
        }
      }

      // Pass the event to the dropdown's keyboard handler to close it
      dropdown.onKeyDown(event);
      return;
    }

    // Handle arrow keys
    if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();

      // Pass the event to the dropdown's keyboard handler
      dropdown.onKeyDown(event);
    }
  }

  /**
   * Handles item selection from dropdown
   */
  onItemSelected(item: DropdownItem): void {
    this.searchText.set(item.label);
    this.valueChange.emit(item.label);
    this.itemSelected.emit(item);
    this.isDropdownOpen.set(false);

    this.isTouched.set(false);

    // Update input value
    const inputEl = this.inputElement();
    if (inputEl) {
      inputEl.nativeElement.value = item.label;
    }
  }

  /**
   * Closes the dropdown
   */
  closeDropdown(): void {
    this.isDropdownOpen.set(false);

    if (!this.searchText()) {
      this.isTouched.set(true);
    }
  }

  /**
   * Clears the search text
   */
  clearSearch(): void {
    this.searchText.set('');
    this.valueChange.emit('');
    this.search.emit('');
    this.isDropdownOpen.set(false);

    // Clear the input element value
    const inputEl = this.inputElement();
    if (inputEl) {
      inputEl.nativeElement.value = '';
      inputEl.nativeElement.focus();
    }
  }

  /**
   * Checks if dropdown should open above or below based on available space
   */
  private checkDropdownPosition(): void {
    const container = this.searchFieldContainer();
    if (!container) return;

    const rect = container.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // If less than 300px space below and more space above, open above
    const dropdownHeight = 300; // approximate dropdown height
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      this.dropdownOpenAbove.set(true);
    } else {
      this.dropdownOpenAbove.set(false);
    }
  }

  /**
   * Handles clicks outside the component to close dropdown
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const container = this.searchFieldContainer();
    if (!container) return;

    const clickedInside = container.nativeElement.contains(
      event.target as Node
    );

    if (!clickedInside && this.isDropdownOpen()) {
      this.closeDropdown();
    }
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}
